import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import { products } from '../data/products';
import { demoPages } from '../data/demoPages';
import { getProductHeroImage } from '../data/productHeroImages';

const API = process.env.REACT_APP_BACKEND_URL;

const REASON_LABELS = {
  complements: 'Pairs well',
  pairs_well: 'Pairs well',
  similar: 'Similar',
  popular: 'Popular',
  trending: 'Trending',
  based_on_browsing: 'For you',
  bundle_for_you: 'Bundle deal',
  save_with_bundle: 'Save with bundle',
  complete_collection: 'Complete set',
};

// Lookup product data from frontend catalog
const getProductData = (slug) => products.find(p => p.slug === slug);

const RecCard = ({ item, placement, onNavigate }) => {
  const cardRef = useRef(null);
  const impressionFired = useRef(false);

  useEffect(() => {
    if (!cardRef.current || impressionFired.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !impressionFired.current) {
          impressionFired.current = true;
          trackEvent('rec_impression', {
            targetSlug: item.slug,
            targetType: item.rec_type,
            metadata: { placement, reason: item.reason },
          });
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [item.slug, item.rec_type, item.reason, placement]);

  const handleClick = () => {
    trackEvent('rec_click', {
      targetSlug: item.slug,
      targetType: item.rec_type,
      metadata: { placement, reason: item.reason },
    });
    sessionStorage.setItem('rec_attribution', JSON.stringify({
      slug: item.slug,
      placement,
      reason: item.reason,
      clicked_at: Date.now(),
    }));
    onNavigate(item);
  };

  const isBundle = item.rec_type === 'bundle';
  const priceStr = item.currency === 'nok' ? `${item.price} kr` : `$${item.price}`;
  const reason = REASON_LABELS[item.reason] || '';

  // Get product visual data
  const productData = getProductData(item.slug);
  const heroImg = getProductHeroImage(item.slug);
  const colors = productData?.colors || { bg: '#F7F5F2', accent: '#B49A88' };

  return (
    <button
      ref={cardRef}
      data-testid={`rec-card-${item.slug}`}
      onClick={handleClick}
      className="flex-shrink-0 w-[220px] text-left group snap-start"
    >
      {/* Product Preview */}
      <div className="relative rounded-xl border border-stone-100 overflow-hidden mb-3 transition-all duration-300 group-hover:border-stone-200 group-hover:shadow-md">
        <div className="aspect-[4/5]">
          {heroImg ? (
            <img
              src={heroImg.url}
              alt={heroImg.alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              style={{ objectPosition: heroImg.focal || 'center' }}
              loading="lazy"
            />
          ) : isBundle ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3" style={{ background: colors.bg }}>
              <Layers className="w-10 h-10" style={{ color: colors.accent + '60' }} />
              <p className="text-[10px] font-sans font-semibold text-center" style={{ color: colors.accent }}>
                {item.product_count || ''} products
              </p>
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-center" style={{ background: colors.bg }}>
              <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ background: colors.accent + '15' }}>
                <span className="font-serif text-2xl font-bold" style={{ color: colors.accent }}>{item.title?.[0]}</span>
              </div>
            </div>
          )}
        </div>
        {/* Badges */}
        {isBundle && item.savings_percent > 0 && (
          <span className="absolute top-2 right-2 text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
            Save {item.savings_percent}%
          </span>
        )}
        {reason && (
          <span className="absolute top-2 left-2 text-[9px] font-medium text-stone-500 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full shadow-sm">
            {reason}
          </span>
        )}
      </div>
      {/* Info */}
      <p className="font-serif text-sm text-stone-800 leading-tight line-clamp-2 mb-1 group-hover:text-stone-900 transition-colors">
        {item.title}
      </p>
      <p className="text-xs font-sans font-semibold text-stone-500">
        {priceStr}
        {isBundle && item.product_count && (
          <span className="font-normal text-stone-400 ml-1">&middot; {item.product_count} products</span>
        )}
      </p>
    </button>
  );
};

const RecommendationSection = ({
  placement = 'product_page',
  contextSlug = '',
  currency = 'usd',
  title = 'Recommended for you',
  subtitle = 'Based on what other customers like',
  limit = 8,
  className = '',
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const navigate = useNavigate();

  const fetchRecs = useCallback(async () => {
    try {
      let url;
      if (placement === 'home') {
        url = `${API}/api/recommendations/home?currency=${currency}&limit=${limit}`;
      } else if (placement === 'post_purchase') {
        url = `${API}/api/recommendations/post-purchase/${contextSlug}?currency=${currency}&limit=${limit}`;
      } else {
        url = `${API}/api/recommendations/product/${contextSlug}?currency=${currency}&limit=${limit}`;
      }
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch {
      // Fail silently
    } finally {
      setLoading(false);
    }
  }, [placement, contextSlug, currency, limit]);

  useEffect(() => { fetchRecs(); }, [fetchRecs]);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState);
    const resizeObs = new ResizeObserver(updateScrollState);
    resizeObs.observe(el);
    return () => { el.removeEventListener('scroll', updateScrollState); resizeObs.disconnect(); };
  }, [items, updateScrollState]);

  if (loading || items.length === 0) return null;

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 260, behavior: 'smooth' });
  };

  const handleNavigate = (item) => {
    if (item.rec_type === 'bundle') {
      navigate(`/?collection=${item.slug}`);
    } else {
      navigate(`/generator?product=${item.slug}`);
    }
  };

  return (
    <section data-testid={`rec-section-${placement}`} className={className}>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h3 className="font-serif text-xl sm:text-2xl text-stone-800 mb-1">{title}</h3>
          {subtitle && <p className="font-sans text-xs sm:text-sm text-stone-400">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {/* Carousel arrows — desktop only */}
          <div className="hidden sm:flex items-center gap-1.5">
            <button
              data-testid="rec-scroll-left"
              onClick={() => scroll(-1)}
              disabled={!canScrollLeft}
              className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all ${
                canScrollLeft ? 'border-stone-200 text-stone-600 hover:bg-stone-50' : 'border-stone-100 text-stone-200 cursor-default'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="rec-scroll-right"
              onClick={() => scroll(1)}
              disabled={!canScrollRight}
              className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all ${
                canScrollRight ? 'border-stone-200 text-stone-600 hover:bg-stone-50' : 'border-stone-100 text-stone-200 cursor-default'
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <button
            data-testid="rec-see-all"
            onClick={() => navigate('/')}
            className="font-sans text-xs font-semibold text-stone-500 hover:text-stone-700 flex items-center gap-1 transition-colors"
          >
            See all <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide scroll-smooth snap-x snap-mandatory"
        data-testid={`rec-scroll-${placement}`}
      >
        {items.map((item) => (
          <RecCard key={item.slug} item={item} placement={placement} onNavigate={handleNavigate} />
        ))}
      </div>
    </section>
  );
};

export default RecommendationSection;
